import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Typography,
  IconButton,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Backdrop,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  CardGiftcard,
  Calculate,
  Save as SaveIcon,
  Warehouse as WarehouseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Percent as PercentIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  Block as BlockIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteOutlineIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import {
  Order,
  OrderFormData,
  ORDER_STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  ShippingAddress,
  ProductStockAvailability,
  CancellationReason,
  ReturnReason,
  CANCELLATION_REASON_LABELS,
  RETURN_REASON_LABELS,
  PaymentStatus,
} from '../../../enums/order';
import { Product, ProductVariant, Warehouse } from '../../../enums/product';
import { OrderService } from './orderService';
import { ProductService } from '../product/productService';
import { useAuth } from '../../../contexts/AuthContext';
import { WarehouseService } from '../warehouse/warehouseService';
import { useCompany } from '../../../contexts/CompanyContext';
import { ShippingService } from '../shipping/shippingService';
import { ShippingCountry, ShippingState } from '../shipping/shipping';
import { InvoiceService } from './invoiceService';

// ─── Shared formatting utilities ──────────────────────────────────────────────
import {
  getCurrencySymbol,
  formatCurrency as sharedFormatCurrency,
  formatNumber,
  formatQuantity,
} from '../../../util/Formatcurrency';

// ─── Refund types ─────────────────────────────────────────────────────────────
type RefundStatus = 'pending' | 'partially_refunded' | 'refunded';
type RefundMethod = 'original_payment' | 'bank_transfer' | 'store_credit' | 'cash' | 'cheque' | 'other';

const REFUND_STATUS_LABELS: Record<RefundStatus, string> = {
  pending: 'Pending',
  partially_refunded: 'Partially Refunded',
  refunded: 'Fully Refunded',
};

const REFUND_METHOD_LABELS: Record<RefundMethod, string> = {
  original_payment: 'Original Payment Method',
  bank_transfer: 'Bank Transfer',
  store_credit: 'Store Credit',
  cash: 'Cash',
  cheque: 'Cheque',
  other: 'Other',
};

// ─── Chip colour helper ───────────────────────────────────────────────────────
const getSoftChipSx = (color: string) => {
  const palette: Record<string, { bg: string; border: string; text: string; iconColor: string }> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };
  const p = palette[color] ?? palette.default;
  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const refundStatusColor = (rs: RefundStatus | undefined): string => {
  if (!rs) return 'default';
  switch (rs) {
    case 'refunded': return 'success';
    case 'partially_refunded': return 'secondary';
    default: return 'warning';
  }
};

// ─── Accepted MIME types for payment proof ────────────────────────────────────
const PAYMENT_PROOF_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';
const PAYMENT_PROOF_MAX_MB = 10;

// ─── Extend Order with optional refund fields ─────────────────────────────────
interface OrderWithRefund extends Order {
  refundStatus?: RefundStatus;
  refundedAmount?: number;
  refundMethod?: RefundMethod;
  refundTransactionId?: string;
  refundDate?: Date;
  refundNotes?: string;
}

// ─── Warehouse allocation types ───────────────────────────────────────────────
interface ItemWarehouseAllocation {
  itemIndex: number;
  productId: string;
  variantId?: string;
  productName: string;
  variantName?: string;
  totalQuantity: number;
  allocations: {
    warehouseId: string;
    warehouseName: string;
    quantity: number;
    availableStock: number;
  }[];
}

// ─── WarehouseSelectionDialog (stable module-level component) ─────────────────
interface WarehouseSelectionDialogProps {
  open: boolean;
  loading: boolean;
  itemAllocations: ItemWarehouseAllocation[];
  stockAvailability: ProductStockAvailability[];
  currencySymbol: string;
  onClose: () => void;
  onConfirm: () => void;
  onUpdateAllocationQuantity: (itemIndex: number, warehouseId: string, quantity: number) => void;
  onAddWarehouse: (itemIndex: number, warehouseId: string) => void;
  onRemoveWarehouse: (itemIndex: number, warehouseId: string) => void;
  validateAllocations: () => { isValid: boolean; errors: string[] };
}

const WarehouseSelectionDialog: React.FC<WarehouseSelectionDialogProps> = ({
  open, loading, itemAllocations, stockAvailability, currencySymbol,
  onClose, onConfirm, onUpdateAllocationQuantity, onAddWarehouse,
  onRemoveWarehouse, validateAllocations,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth keepMounted={false} disablePortal={false}>
    <DialogTitle>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarehouseIcon />
        Multi-Warehouse Order Allocation
      </Box>
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Allocate products from different warehouses. You can split quantities across multiple warehouses.
      </Typography>
      {itemAllocations.map((item, itemIndex) => {
        const totalAllocated = item.allocations.reduce((sum, a) => sum + a.quantity, 0);
        const isFullyAllocated = totalAllocated === item.totalQuantity;
        const hasOverAllocation = totalAllocated > item.totalQuantity;
        return (
          <Paper key={itemIndex} variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'background.paper', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {item.productName}
                  {item.variantName && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({item.variantName})
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Required: {formatQuantity(item.totalQuantity)} units
                </Typography>
              </Box>
              <Chip
                label={
                  hasOverAllocation
                    ? `Over-allocated: ${formatQuantity(totalAllocated)}/${formatQuantity(item.totalQuantity)}`
                    : isFullyAllocated ? 'Fully Allocated'
                      : `Allocated: ${formatQuantity(totalAllocated)}/${formatQuantity(item.totalQuantity)}`
                }
                color={hasOverAllocation ? 'error' : isFullyAllocated ? 'success' : 'warning'}
                icon={hasOverAllocation ? <WarningIcon /> : isFullyAllocated ? <CheckCircleIcon /> : <InfoIcon />}
              />
            </Box>
            {item.allocations.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.selected', '& th': { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' } }}>
                      <TableCell>Warehouse</TableCell>
                      <TableCell align="right">Allocated Quantity</TableCell>
                      <TableCell align="right">Available Stock</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.allocations.map((allocation, allocIndex) => {
                      const exceedsStock = allocation.quantity > allocation.availableStock;
                      return (
                        <TableRow key={`${allocation.warehouseId}-${allocIndex}`} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{allocation.warehouseName}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number" size="small" value={allocation.quantity}
                              onChange={(e) => onUpdateAllocationQuantity(itemIndex, allocation.warehouseId, parseInt(e.target.value) || 0)}
                              inputProps={{ min: 0, max: allocation.availableStock }}
                              error={exceedsStock} sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={exceedsStock ? 'error' : 'text.secondary'}>
                              {formatQuantity(allocation.availableStock)} units
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => onRemoveWarehouse(itemIndex, allocation.warehouseId)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <InputLabel>Add Warehouse</InputLabel>
                <Select label="Add Warehouse" value="" onChange={(e) => onAddWarehouse(itemIndex, e.target.value)}>
                  {stockAvailability[itemIndex]?.warehouses
                    .filter(w => !item.allocations.some(a => a.warehouseId === w.warehouseId))
                    .map(warehouse => (
                      <MenuItem key={warehouse.warehouseId} value={warehouse.warehouseId}>
                        {warehouse.warehouseName} ({formatQuantity(warehouse.availableQuantity)} available)
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">Select a warehouse to add to allocation</Typography>
            </Box>
            {!isFullyAllocated && !hasOverAllocation && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {formatQuantity(item.totalQuantity - totalAllocated)} units still need to be allocated
              </Alert>
            )}
            {hasOverAllocation && (
              <Alert severity="error" sx={{ mt: 2 }}>
                You have over-allocated by {formatQuantity(totalAllocated - item.totalQuantity)} units. Please adjust.
              </Alert>
            )}
          </Paper>
        );
      })}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        onClick={onConfirm} variant="contained"
        disabled={loading || !validateAllocations().isValid}
        startIcon={loading ? <CircularProgress size={20} /> : <WarehouseIcon />}
      >
        {loading ? 'Processing...' : 'Confirm Allocation'}
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── OrderDrawer props ────────────────────────────────────────────────────────
interface OrderDrawerProps {
  isOpen: boolean;
  mode: 'add' | 'edit' | 'view';
  order: OrderWithRefund | null;
  onClose: () => void;
  onSave: () => void;
  onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

// ─── Address helpers ──────────────────────────────────────────────────────────
const resolveCountry = (value: string, countries: ShippingCountry[]): ShippingCountry | undefined => {
  if (!value) return undefined;
  return countries.find(c => c.name === value) ?? countries.find(c => String(c.id) === String(value));
};

const resolveState = (value: string, states: ShippingState[]): ShippingState | undefined => {
  if (!value) return undefined;
  return states.find(s => s.name === value) ?? states.find(s => String(s.id) === String(value));
};

const normaliseStateToName = (value: string, states: ShippingState[]): string => {
  if (!value) return '';
  return resolveState(value, states)?.name ?? value;
};

// ─── Helper: check if a product is fully out of stock ────────────────────────
const isProductOutOfStock = (product: Product): boolean => {
  if (product.hasVariants && product.variants?.length) {
    return product.variants.every(v => v.isOutOfStock || v.stockQuantity <= 0);
  }
  return product.isOutOfStock || product.stockQuantity <= 0;
};

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader: React.FC<{
  icon?: React.ReactNode; title: string; action?: React.ReactNode; subtitle?: string;
}> = ({ icon, title, action, subtitle }) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{icon}</Box>}
        <Typography variant="subtitle1" fontWeight={600} color="text.primary">{title}</Typography>
      </Box>
      {action}
    </Box>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ ml: icon ? 4 : 0 }}>{subtitle}</Typography>
    )}
    <Divider sx={{ mt: 1 }} />
  </Box>
);

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
  <Box sx={{ mb: 4, ...sx }}>{children}</Box>
);

// ─── Main OrderDrawer ─────────────────────────────────────────────────────────
const OrderDrawer: React.FC<OrderDrawerProps> = ({
  isOpen, mode, order, onClose, onSave, onShowToast,
}) => {
  const { currentUser } = useAuth();
  const { companyProfile } = useCompany();

  const [formData, setFormData] = useState<OrderFormData>({
    customerName: '', customerEmail: '', customerPhone: '', customerWhatsApp: '',
    items: [{ productId: '', variantId: '', quantity: '1' }],
    subtotal: '0', taxAmount: '0', shippingCost: '0', discountAmount: '0',
    status: 'pending', paymentStatus: 'pending', paymentMethod: 'credit_card',
    shippingAddress: { fullName: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', country: '', phoneNumber: '' },
    billingAddress: { fullName: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', country: '', phoneNumber: '' },
    notes: '', trackingNumber: '', estimatedDelivery: '',
    paymentProofUrl: undefined, paymentProofType: undefined, paymentProofFileName: undefined,
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [stockAvailability, setStockAvailability] = useState<ProductStockAvailability[]>([]);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState<CancellationReason>('customer_request');
  const [returnReason, setReturnReason] = useState<ReturnReason>('changed_mind');
  const [damageNotes, setDamageNotes] = useState('');
  const [itemAllocations, setItemAllocations] = useState<ItemWarehouseAllocation[]>([]);

  const [shippingCountries, setShippingCountries] = useState<ShippingCountry[]>([]);
  const [shippingStates, setShippingStates] = useState<ShippingState[]>([]);
  const [billingStates, setBillingStates] = useState<ShippingState[]>([]);
  const [loadingShipping, setLoadingShipping] = useState(false);

  const [initialStatus, setInitialStatus] = useState<string>('');

  const [taxType, setTaxType] = useState<'fixed' | 'percentage'>('percentage');
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('percentage');
  const [taxPercentage, setTaxPercentage] = useState('0');
  const [discountPercentage, setDiscountPercentage] = useState('0');

  const [refundStatus, setRefundStatus] = useState<RefundStatus>('pending');
  const [refundedAmount, setRefundedAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState<RefundMethod>('original_payment');
  const [refundTransactionId, setRefundTransactionId] = useState('');
  const [refundDate, setRefundDate] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  // ─── Payment proof state ───────────────────────────────────────────────────
  // pendingProofFile  — File selected but not yet uploaded (held in memory until save)
  // pendingProofPreview — Object URL for image preview of pendingProofFile
  // proofUploadProgress — 0-100 while uploading, null otherwise
  // deletingProof — true while a delete operation is in flight
  const [pendingProofFile, setPendingProofFile] = useState<File | null>(null);
  const [pendingProofPreview, setPendingProofPreview] = useState<string | null>(null);
  const [proofUploadProgress, setProofUploadProgress] = useState<number | null>(null);
  const [deletingProof, setDeletingProof] = useState(false);
  const proofInputRef = useRef<HTMLInputElement>(null);

  const isPopulatingRef = useRef(false);

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);
  const companyTaxRate = companyProfile?.taxPercentage ?? 0;

  const formatCurrency = (amount: number | string): string => sharedFormatCurrency(amount, currencySymbol);
  const formatPriceDisplay = (price: number | string): string => sharedFormatCurrency(price, currencySymbol);

  const formatDate = (date?: Date | string): string => {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const refundAmountEditable = refundStatus !== 'pending';

  // ─── Derived: is bank transfer currently selected ─────────────────────────
  const isBankTransfer = formData.paymentMethod === 'bank_transfer';

  // ─── Derived: currently saved proof (from formData, i.e. from Firestore) ──
  const savedProofUrl = formData.paymentProofUrl;
  const savedProofType = formData.paymentProofType;
  const savedProofFileName = formData.paymentProofFileName;

  // ─── Clean up object URL when component unmounts or file changes ──────────
  useEffect(() => {
    return () => {
      if (pendingProofPreview) URL.revokeObjectURL(pendingProofPreview);
    };
  }, [pendingProofPreview]);

  // ─── When payment method changes away from bank_transfer, clear pending ───
  useEffect(() => {
    if (!isBankTransfer) {
      setPendingProofFile(null);
      if (pendingProofPreview) {
        URL.revokeObjectURL(pendingProofPreview);
        setPendingProofPreview(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBankTransfer]);

  // ─── Compute blended discount % ──────────────────────────────────────────
  const computeItemsDiscountPercentage = (
    items: { productId: string; variantId?: string; quantity: string }[]
  ): number => {
    let totalValue = 0;
    let totalDiscount = 0;
    for (const item of items) {
      if (!item.productId) continue;
      const product = products.find(p => String(p.id) === String(item.productId));
      if (!product) continue;
      const qty = parseInt(item.quantity) || 0;
      if (qty <= 0) continue;
      let unitPrice = product.price;
      let discountPct = product.discount || 0;
      if (product.hasVariants && product.variants?.length && item.variantId) {
        const variant = findVariantByKey(product.variants, item.variantId);
        if (variant) { unitPrice = variant.price; discountPct = variant.discount || 0; }
      }
      const lineValue = unitPrice * qty;
      totalValue += lineValue;
      totalDiscount += lineValue * (discountPct / 100);
    }
    if (totalValue === 0) return 0;
    return (totalDiscount / totalValue) * 100;
  };

  // ─── Data loading ─────────────────────────────────────────────────────────
  const loadShippingCountries = async () => {
    try {
      setLoadingShipping(true);
      const countries = await ShippingService.getAll();
      setShippingCountries(countries);
      return countries;
    } catch (error) {
      console.error('Error loading shipping countries:', error);
      onShowToast('Failed to load shipping countries', 'error');
      return [];
    } finally {
      setLoadingShipping(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const warehousesData = await WarehouseService.getActiveWarehouses();
      setWarehouses(warehousesData);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      onShowToast('Failed to load warehouses', 'error');
    }
  };

  useEffect(() => {
    if (isOpen) {
      const loadInitialData = async () => {
        setInitialLoading(true);
        try {
          await Promise.all([loadWarehouses(), loadShippingCountries(), fetchProducts()]);
        } catch (error) {
          console.error('Error loading initial data:', error);
        } finally {
          setInitialLoading(false);
        }
      };
      loadInitialData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const populateForm = async () => {
      if (order && (mode === 'edit' || mode === 'view')) {
        await populateFormData();
        setInitialStatus(order.status);
      } else {
        resetForm();
        setInitialStatus('');
      }
    };
    if (products.length > 0 || mode === 'add') populateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order, mode, products]);

  useEffect(() => {
    if (isOpen && mode === 'add' && companyTaxRate > 0) {
      setTaxType('percentage');
      setTaxPercentage(String(companyTaxRate));
    }
  }, [isOpen, mode, companyTaxRate]);

  // ─── Variant key helpers ──────────────────────────────────────────────────
  const getVariantKey = (v: ProductVariant): string => {
    if (v.id !== undefined && v.id !== null && v.id !== '') return `id:${v.id}`;
    if (v.sku && v.sku.trim() !== '') return `sku:${v.sku.trim()}`;
    if (v.variantSelections?.length) {
      const selections = v.variantSelections.map(s => `${s.variantType}:${s.variantValue}`).sort().join('|');
      return `sel:${selections}`;
    }
    return `fallback:${Math.random().toString(36).slice(2, 9)}`;
  };

  const findVariantByKey = (variants: ProductVariant[], key: string): ProductVariant | undefined =>
    variants.find(v => getVariantKey(v) === key);

  // ─── Stock / allocation ───────────────────────────────────────────────────
  const checkStockAvailability = async () => {
    try {
      const itemsForCheck = formData.items.filter(item => item.productId);
      if (itemsForCheck.length === 0) return;
      // If this order was already allocated (e.g. auto-reserved when the
      // customer placed it), its held stock isn't "available" in Firestore
      // anymore — pass the existing items along so it's added back into the
      // availability numbers and can be offered as the default warehouse.
      const currentlyReservedItems = mode === 'edit' && order?.isWarehouseAllocated ? order.items : undefined;
      const stockCheck = await OrderService.checkStockAvailabilityAcrossWarehouses(
        itemsForCheck.map(item => ({ productId: item.productId, variantId: item.variantId, quantity: item.quantity })),
        currentlyReservedItems
      );
      setStockAvailability(stockCheck.availability);
      initializeItemAllocations(stockCheck.availability, currentlyReservedItems);
      return stockCheck;
    } catch (error) {
      console.error('Error checking stock:', error);
      onShowToast('Failed to check stock availability', 'error');
      return null;
    }
  };

  const initializeItemAllocations = (availability: ProductStockAvailability[], currentlyReservedItems?: Order['items']) => {
    const allocations: ItemWarehouseAllocation[] = availability.map((item, index) => {
      const autoAllocations: ItemWarehouseAllocation['allocations'] = [];
      let remaining = item.requestedQuantity;

      // Default to the warehouse this order was already allocated to, if any.
      const existingWarehouseId = currentlyReservedItems?.find(
        oi => oi.sourceWarehouseId && oi.productId === item.productId && (oi.variantId || undefined) === item.variantId
      )?.sourceWarehouseId;
      if (existingWarehouseId) {
        const wh = item.warehouses.find(w => w.warehouseId === existingWarehouseId);
        if (wh && wh.availableQuantity > 0) {
          const qty = Math.min(wh.availableQuantity, remaining);
          autoAllocations.push({ warehouseId: wh.warehouseId, warehouseName: wh.warehouseName, quantity: qty, availableStock: wh.availableQuantity });
          remaining -= qty;
        }
      }

      if (remaining > 0) {
        const sortedWarehouses = [...item.warehouses]
          .filter(wh => !autoAllocations.some(a => a.warehouseId === wh.warehouseId))
          .sort((a, b) => b.availableQuantity - a.availableQuantity);
        for (const wh of sortedWarehouses) {
          if (remaining <= 0) break;
          const qty = Math.min(wh.availableQuantity, remaining);
          if (qty > 0) {
            autoAllocations.push({ warehouseId: wh.warehouseId, warehouseName: wh.warehouseName, quantity: qty, availableStock: wh.availableQuantity });
            remaining -= qty;
          }
        }
      }

      return { itemIndex: index, productId: item.productId, variantId: item.variantId, productName: item.productName, variantName: item.variantName, totalQuantity: item.requestedQuantity, allocations: autoAllocations };
    });
    setItemAllocations(allocations);
  };

  const updateAllocationQuantity = (itemIndex: number, warehouseId: string, quantity: number) => {
    setItemAllocations(prev => {
      const updated = prev.map(item => ({ ...item, allocations: [...item.allocations] }));
      const item = updated[itemIndex];
      const idx = item.allocations.findIndex(a => a.warehouseId === warehouseId);
      if (idx >= 0) {
        if (quantity > 0) item.allocations[idx] = { ...item.allocations[idx], quantity };
        else item.allocations.splice(idx, 1);
      } else if (quantity > 0) {
        const wh = stockAvailability[itemIndex].warehouses.find(w => w.warehouseId === warehouseId);
        if (wh) item.allocations.push({ warehouseId: wh.warehouseId, warehouseName: wh.warehouseName, quantity, availableStock: wh.availableQuantity });
      }
      return updated;
    });
  };

  const addWarehouseToItem = (itemIndex: number, warehouseId: string) => {
    const item = itemAllocations[itemIndex];
    if (item.allocations.some(a => a.warehouseId === warehouseId)) return;
    updateAllocationQuantity(itemIndex, warehouseId, 1);
  };

  const removeWarehouseFromItem = (itemIndex: number, warehouseId: string) => {
    setItemAllocations(prev =>
      prev.map((item, i) =>
        i === itemIndex ? { ...item, allocations: item.allocations.filter(a => a.warehouseId !== warehouseId) } : item
      )
    );
  };

  const validateAllocations = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    itemAllocations.forEach(item => {
      const totalAllocated = item.allocations.reduce((sum, a) => sum + a.quantity, 0);
      if (totalAllocated !== item.totalQuantity) {
        errors.push(`${item.productName}${item.variantName ? ` (${item.variantName})` : ''}: Allocated ${formatQuantity(totalAllocated)} but need ${formatQuantity(item.totalQuantity)}`);
      }
      item.allocations.forEach(allocation => {
        if (allocation.quantity > allocation.availableStock) {
          errors.push(`${item.productName} at ${allocation.warehouseName}: Allocated ${formatQuantity(allocation.quantity)} but only ${formatQuantity(allocation.availableStock)} available`);
        }
      });
    });
    return { isValid: errors.length === 0, errors };
  };

  // ─── Invoice ──────────────────────────────────────────────────────────────
  const handleDownloadInvoice = async () => {
    if (!order) return;
    try {
      setDownloadingInvoice(true);
      await InvoiceService.generateInvoice(order, companyProfile);
      onShowToast('Invoice downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      onShowToast('Failed to download invoice. Please try again.', 'error');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  // ─── Products ─────────────────────────────────────────────────────────────
  const fetchProducts = async (): Promise<Product[]> => {
    try {
      const data = await ProductService.getProducts();
      const normalized: Product[] = (data || []).map((p: Product) => ({
        ...p,
        id: p.id ? String(p.id) : '',
        variants: (p.variants || []).map(v => ({ ...v, id: v.id ? String(v.id) : undefined })),
      }));
      setProducts(normalized);
      return normalized;
    } catch (err) {
      console.error('Error fetching products:', err);
      onShowToast('Failed to load products', 'error');
      setProducts([]);
      return [];
    }
  };

  // ─── Populate form ────────────────────────────────────────────────────────
  const populateFormData = async () => {
    if (!order) return;
    isPopulatingRef.current = true;

    const rawShipCountry = order.shippingAddress.country || '';
    const rawBillCountry = order.billingAddress?.country || order.shippingAddress.country || '';

    const resolvedShipCountry = resolveCountry(rawShipCountry, shippingCountries);
    const resolvedBillCountry = resolveCountry(rawBillCountry, shippingCountries);

    const shipStates: ShippingState[] = resolvedShipCountry?.states || [];
    const billStates: ShippingState[] = resolvedBillCountry?.states || [];
    setShippingStates(shipStates);
    setBillingStates(billStates);

    const shippingCountryName = resolvedShipCountry?.name || rawShipCountry;
    const billingCountryName = resolvedBillCountry?.name || rawBillCountry;
    const shippingStateName = normaliseStateToName(order.shippingAddress.state || '', shipStates);
    const billingStateName = normaliseStateToName(order.billingAddress?.state || order.shippingAddress.state || '', billStates);

    const mappedItems = order.items.map(i => {
      const productId = String(i.productId || '');
      let mappedVariantId = i.variantId ? String(i.variantId) : '';
      if (i.variantId) {
        const prod = products.find(p => String(p.id) === productId);
        if (prod?.variants) {
          const found = prod.variants.find(v => {
            const key = getVariantKey(v);
            return key === String(i.variantId) || (v.id && String(v.id) === String(i.variantId)) || (v.sku && v.sku === i.variantId);
          });
          if (found) mappedVariantId = getVariantKey(found);
        }
      }
      return { productId, variantId: mappedVariantId, quantity: i.quantity.toString() };
    });

    const subtotal = order.subtotal || 0;
    const taxAmount = order.taxAmount || 0;
    const discountAmount = order.discountAmount || 0;

    let calculatedTaxPercentage = '0';
    let calculatedDiscountPercentage = '0';
    let determinedTaxType: 'fixed' | 'percentage' = 'fixed';
    let determinedDiscountType: 'fixed' | 'percentage' = 'fixed';

    if (subtotal > 0) {
      const taxPercent = (taxAmount / subtotal) * 100;
      const originalSubtotal = subtotal + discountAmount;
      const discountPercent = originalSubtotal > 0 ? (discountAmount / originalSubtotal) * 100 : 0;
      const taxPctR = Math.round(taxPercent * 100) / 100;
      const discPctR = Math.round(discountPercent * 100) / 100;

      if (Math.abs((subtotal * taxPctR) / 100 - taxAmount) < 0.01) {
        determinedTaxType = 'percentage'; calculatedTaxPercentage = taxPctR.toFixed(2);
      } else { calculatedTaxPercentage = taxPctR.toFixed(2); }

      if (Math.abs((originalSubtotal * discPctR) / 100 - discountAmount) < 0.01) {
        determinedDiscountType = 'percentage'; calculatedDiscountPercentage = discPctR.toFixed(2);
      } else { calculatedDiscountPercentage = discPctR.toFixed(2); }
    }

    const addressesAreSame: boolean = !!(
      order.billingAddress &&
      order.billingAddress.fullName === order.shippingAddress.fullName &&
      order.billingAddress.addressLine1 === order.shippingAddress.addressLine1 &&
      (order.billingAddress.addressLine2 || '') === (order.shippingAddress.addressLine2 || '') &&
      order.billingAddress.city === order.shippingAddress.city &&
      (resolveState(order.billingAddress.state || '', billStates)?.name || order.billingAddress.state) ===
      (resolveState(order.shippingAddress.state || '', shipStates)?.name || order.shippingAddress.state) &&
      order.billingAddress.postalCode === order.shippingAddress.postalCode &&
      (resolveCountry(order.billingAddress.country || '', shippingCountries)?.name || order.billingAddress.country) ===
      (resolveCountry(order.shippingAddress.country || '', shippingCountries)?.name || order.shippingAddress.country) &&
      (order.billingAddress.phoneNumber || '') === (order.shippingAddress.phoneNumber || '')
    );

    setUseSameAddress(addressesAreSame);
    setTaxType(determinedTaxType);
    setDiscountType(determinedDiscountType);
    setTaxPercentage(calculatedTaxPercentage);
    setDiscountPercentage(calculatedDiscountPercentage);

    const normalisedShipping: ShippingAddress = { ...order.shippingAddress, country: shippingCountryName, state: shippingStateName };
    const rawBillingAddr = order.billingAddress || order.shippingAddress;
    const normalisedBilling: ShippingAddress = { ...rawBillingAddr, country: billingCountryName, state: billingStateName };

    setFormData({
      customerName: order.customerName,
      customerEmail: order.customerEmail,
      customerPhone: order.customerPhone || '',
      customerWhatsApp: order.customerWhatsApp,
      items: mappedItems.length ? mappedItems : [{ productId: '', variantId: '', quantity: '1' }],
      subtotal: order.subtotal.toString(),
      taxAmount: order.taxAmount.toString(),
      shippingCost: order.shippingCost.toString(),
      discountAmount: order.discountAmount.toString(),
      status: order.status,
      paymentStatus: order.paymentStatus,
      paymentMethod: order.paymentMethod,
      shippingAddress: normalisedShipping,
      billingAddress: normalisedBilling,
      notes: order.notes || '',
      trackingNumber: order.trackingNumber || '',
      estimatedDelivery: order.estimatedDelivery
        ? (order.estimatedDelivery instanceof Date
          ? order.estimatedDelivery.toISOString().split('T')[0]
          : new Date(order.estimatedDelivery).toISOString().split('T')[0])
        : '',
      // Payment proof — restore from saved order
      paymentProofUrl: order.paymentProofUrl || undefined,
      paymentProofType: order.paymentProofType || undefined,
      paymentProofFileName: order.paymentProofFileName || undefined,
    });

    setRefundStatus((order as OrderWithRefund).refundStatus || 'pending');
    setRefundedAmount((order as OrderWithRefund).refundedAmount !== undefined ? String((order as OrderWithRefund).refundedAmount) : '');
    setRefundMethod((order as OrderWithRefund).refundMethod || 'original_payment');
    setRefundTransactionId((order as OrderWithRefund).refundTransactionId || '');
    setRefundDate(
      (order as OrderWithRefund).refundDate
        ? ((order as OrderWithRefund).refundDate! instanceof Date
          ? (order as OrderWithRefund).refundDate!.toISOString().split('T')[0]
          : new Date((order as OrderWithRefund).refundDate as any).toISOString().split('T')[0])
        : ''
    );
    setRefundNotes((order as OrderWithRefund).refundNotes || '');

    // Clear any pending file selection when opening an existing order
    setPendingProofFile(null);
    if (pendingProofPreview) { URL.revokeObjectURL(pendingProofPreview); setPendingProofPreview(null); }

    setTimeout(() => { isPopulatingRef.current = false; }, 0);
  };

  const resetForm = () => {
    const initialTaxPct = companyTaxRate > 0 ? String(companyTaxRate) : '0';
    setFormData({
      customerName: '', customerEmail: '', customerPhone: '', customerWhatsApp: '',
      items: [{ productId: '', variantId: '', quantity: '1' }],
      subtotal: '0', taxAmount: '0', shippingCost: '0', discountAmount: '0',
      status: 'pending', paymentStatus: 'pending', paymentMethod: 'credit_card',
      shippingAddress: { fullName: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', country: '', phoneNumber: '' },
      billingAddress: { fullName: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', country: '', phoneNumber: '' },
      notes: '', trackingNumber: '', estimatedDelivery: '',
      paymentProofUrl: undefined, paymentProofType: undefined, paymentProofFileName: undefined,
    });
    setUseSameAddress(true);
    setShippingStates([]);
    setBillingStates([]);
    setItemAllocations([]);
    setInitialStatus('');
    setTaxType('percentage');
    setDiscountType('percentage');
    setTaxPercentage(initialTaxPct);
    setDiscountPercentage('0');
    setRefundStatus('pending');
    setRefundedAmount('');
    setRefundMethod('original_payment');
    setRefundTransactionId('');
    setRefundDate('');
    setRefundNotes('');
    setPendingProofFile(null);
    if (pendingProofPreview) { URL.revokeObjectURL(pendingProofPreview); setPendingProofPreview(null); }
  };

  // ─── Form handlers ────────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string) => (e: any) => {
    setFormData(prev => ({ ...prev, [name]: e.target.value }));
  };

  const handleAddressChange = (
    addressType: 'shippingAddress' | 'billingAddress',
    field: keyof ShippingAddress,
    value: string
  ) => {
    setFormData(prev => ({ ...prev, [addressType]: { ...prev[addressType], [field]: value } }));

    if (field === 'country') {
      const selectedCountry = shippingCountries.find(c => c.name === value);
      if (selectedCountry) {
        if (addressType === 'shippingAddress') {
          setShippingStates(selectedCountry.states || []);
          if (formData.shippingCost === '0' || formData.shippingCost === '') {
            setFormData(prev => ({ ...prev, shippingCost: selectedCountry.defaultShippingCost.toString() }));
          }
        } else {
          setBillingStates(selectedCountry.states || []);
        }
      } else {
        if (addressType === 'shippingAddress') setShippingStates([]);
        else setBillingStates([]);
      }
      setFormData(prev => ({ ...prev, [addressType]: { ...prev[addressType], [field]: value, state: '' } }));
    }

    if (field === 'state' && addressType === 'shippingAddress') {
      const selectedCountry = shippingCountries.find(c => c.name === formData.shippingAddress.country);
      if (selectedCountry) {
        const selectedState = selectedCountry.states?.find(s => s.name === value);
        if (selectedState?.shippingCost) {
          setFormData(prev => ({ ...prev, shippingCost: selectedState.shippingCost.toString() }));
        }
      }
    }

    if (addressType === 'shippingAddress' && useSameAddress) {
      setFormData(prev => ({
        ...prev,
        billingAddress: {
          ...prev.shippingAddress,
          [field]: field === 'country' ? value : prev.shippingAddress[field],
          state: field === 'country' ? '' : (field === 'state' ? value : prev.shippingAddress.state),
        },
      }));
      if (field === 'country') {
        const selectedCountry = shippingCountries.find(c => c.name === value);
        if (selectedCountry) setBillingStates(selectedCountry.states || []);
      }
    }
  };

  const handleItemChange = (index: number, field: 'productId' | 'variantId' | 'quantity', value: string) => {
    const newItems = [...formData.items];
    if (field === 'productId') {
      newItems[index] = { productId: String(value), variantId: '', quantity: newItems[index]?.quantity || '1' };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setFormData(prev => ({ ...prev, items: newItems }));
    calculateTotals(newItems);
    if (field === 'productId' || field === 'variantId') _applyItemsDiscount(newItems);
  };

  const _applyItemsDiscount = (items: { productId: string; variantId?: string; quantity: string }[]) => {
    const blended = computeItemsDiscountPercentage(items);
    setDiscountType('percentage');
    setDiscountPercentage(blended.toFixed(2));
  };

  const addItem = () => {
    setFormData(prev => ({ ...prev, items: [...prev.items, { productId: '', variantId: '', quantity: '1' }] }));
  };

  const removeItem = (index: number) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: newItems }));
    calculateTotals(newItems);
    _applyItemsDiscount(newItems);
  };

  const calculateTotals = async (items: { productId: string; variantId?: string; quantity: string }[]) => {
    try {
      let originalSubtotal = 0;
      for (const item of items) {
        if (item.productId && item.quantity) {
          const product = products.find(p => String(p.id) === String(item.productId));
          if (product) {
            let unitPrice = product.price;
            if (product.hasVariants && product.variants?.length && item.variantId) {
              const variant = findVariantByKey(product.variants, item.variantId);
              if (variant) unitPrice = variant.price;
            }
            originalSubtotal += unitPrice * parseInt(item.quantity);
          }
        }
      }
      let discountAmount = 0;
      if (discountType === 'percentage') {
        discountAmount = (originalSubtotal * (parseFloat(discountPercentage) || 0)) / 100;
      } else {
        discountAmount = parseFloat(formData.discountAmount) || 0;
      }
      const subtotal = originalSubtotal - discountAmount;
      let taxAmount = 0;
      if (taxType === 'percentage') {
        taxAmount = (subtotal * (parseFloat(taxPercentage) || 0)) / 100;
      } else {
        taxAmount = parseFloat(formData.taxAmount) || 0;
      }
      setFormData(prev => ({
        ...prev,
        subtotal: subtotal.toFixed(2),
        taxAmount: taxAmount.toFixed(2),
        discountAmount: discountAmount.toFixed(2),
      }));
    } catch (error) {
      console.error('Error calculating totals:', error);
      onShowToast('Error calculating order totals', 'error');
    }
  };

  useEffect(() => {
    if (isPopulatingRef.current) return;
    if (formData.items.length > 0) calculateTotals(formData.items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxType, taxPercentage, discountType, discountPercentage]);

  const handleTaxTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: 'fixed' | 'percentage' | null) => {
    if (newType !== null) {
      setTaxType(newType);
      if (newType === 'percentage') {
        const subtotal = parseFloat(formData.subtotal) || 0;
        const pct = subtotal > 0 ? ((parseFloat(formData.taxAmount) || 0) / subtotal) * 100 : 0;
        setTaxPercentage(pct.toFixed(2));
      } else {
        const subtotal = parseFloat(formData.subtotal) || 0;
        const taxAmt = (subtotal * (parseFloat(taxPercentage) || 0)) / 100;
        setFormData(prev => ({ ...prev, taxAmount: taxAmt.toFixed(2) }));
      }
    }
  };

  const handleDiscountTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: 'fixed' | 'percentage' | null) => {
    if (newType !== null) {
      setDiscountType(newType);
      if (newType === 'percentage') {
        const subtotal = parseFloat(formData.subtotal) || 0;
        const discAmt = parseFloat(formData.discountAmount) || 0;
        const origSub = subtotal + discAmt;
        const pct = origSub > 0 ? (discAmt / origSub) * 100 : 0;
        setDiscountPercentage(pct.toFixed(2));
      } else {
        const subtotal = parseFloat(formData.subtotal) || 0;
        const discAmt = parseFloat(formData.discountAmount) || 0;
        const origSub = subtotal + discAmt;
        const discNew = (origSub * (parseFloat(discountPercentage) || 0)) / 100;
        setFormData(prev => ({ ...prev, discountAmount: discNew.toFixed(2) }));
      }
    }
  };

  const handleTaxPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTaxPercentage(value);
    const subtotal = parseFloat(formData.subtotal) || 0;
    const taxAmt = (subtotal * (parseFloat(value) || 0)) / 100;
    setFormData(prev => ({ ...prev, taxAmount: taxAmt.toFixed(2) }));
  };

  const handleDiscountPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDiscountPercentage(value);
    const postDisc = parseFloat(formData.subtotal) || 0;
    const curDisc = parseFloat(formData.discountAmount) || 0;
    const origSub = postDisc + curDisc;
    const discAmt = (origSub * (parseFloat(value) || 0)) / 100;
    const newSubtotal = origSub - discAmt;
    setFormData(prev => ({ ...prev, subtotal: newSubtotal.toFixed(2), discountAmount: discAmt.toFixed(2) }));
  };

  const handleTaxFixedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, taxAmount: value }));
    const subtotal = parseFloat(formData.subtotal) || 0;
    const pct = subtotal > 0 ? ((parseFloat(value) || 0) / subtotal) * 100 : 0;
    setTaxPercentage(pct.toFixed(2));
  };

  const handleDiscountFixedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, discountAmount: value }));
    const subtotal = parseFloat(formData.subtotal) || 0;
    const discAmt = parseFloat(value) || 0;
    const origSub = subtotal + discAmt;
    const pct = origSub > 0 ? (discAmt / origSub) * 100 : 0;
    setDiscountPercentage(pct.toFixed(2));
  };

  const handleUseSameAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setUseSameAddress(checked);
    if (checked) {
      setFormData(prev => ({ ...prev, billingAddress: { ...prev.shippingAddress } }));
      setBillingStates([...shippingStates]);
    } else {
      setFormData(prev => ({
        ...prev,
        billingAddress: prev.billingAddress || { fullName: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', country: '', phoneNumber: '' },
      }));
    }
  };

  // ─── Payment proof handlers ───────────────────────────────────────────────

  /**
   * Called when the user picks a file via the hidden <input>.
   * We only store it in state — the actual upload happens on Save so we
   * always have an orderId to use as the storage path prefix.
   */
  const handleProofFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      onShowToast('Only images (JPEG, PNG, WebP, GIF) and PDF files are allowed', 'error');
      return;
    }

    // Validate size
    const maxBytes = PAYMENT_PROOF_MAX_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      onShowToast(`File size must be under ${PAYMENT_PROOF_MAX_MB} MB`, 'error');
      return;
    }

    // Revoke any previous preview
    if (pendingProofPreview) URL.revokeObjectURL(pendingProofPreview);

    setPendingProofFile(file);

    if (file.type !== 'application/pdf') {
      setPendingProofPreview(URL.createObjectURL(file));
    } else {
      setPendingProofPreview(null);
    }

    // Reset the input so the same file can be re-selected if needed
    if (proofInputRef.current) proofInputRef.current.value = '';
  };

  /**
   * Uploads the pending file to Firebase Storage and saves the URL +
   * metadata back to the order document and local formData.
   * Only available in edit mode when an orderId exists.
   */
  const handleUploadProof = async () => {
    if (!pendingProofFile || !order?.id) return;
    try {
      setProofUploadProgress(0);
      const result = await OrderService.uploadPaymentProof(
        pendingProofFile,
        order.id,
        (pct) => setProofUploadProgress(pct)
      );
      // Save to Firestore immediately
      await OrderService.savePaymentProofToOrder(order.id, result);
      // Update local formData so the UI reflects the new attachment
      setFormData(prev => ({
        ...prev,
        paymentProofUrl: result.url,
        paymentProofType: result.type,
        paymentProofFileName: result.fileName,
      }));
      // Clear the pending selection
      setPendingProofFile(null);
      if (pendingProofPreview) { URL.revokeObjectURL(pendingProofPreview); setPendingProofPreview(null); }
      onShowToast('Payment proof uploaded successfully', 'success');
    } catch (err: any) {
      onShowToast(err.message || 'Failed to upload payment proof', 'error');
    } finally {
      setProofUploadProgress(null);
    }
  };

  /**
   * Removes the saved attachment from Firebase Storage and clears the
   * metadata from the order document and local formData.
   */
  const handleDeleteProof = async () => {
    if (!savedProofUrl || !order?.id) return;
    try {
      setDeletingProof(true);
      await OrderService.deletePaymentProof(savedProofUrl);
      await OrderService.savePaymentProofToOrder(order.id, null);
      setFormData(prev => ({
        ...prev,
        paymentProofUrl: undefined,
        paymentProofType: undefined,
        paymentProofFileName: undefined,
      }));
      onShowToast('Payment proof deleted', 'success');
    } catch (err: any) {
      onShowToast(err.message || 'Failed to delete payment proof', 'error');
    } finally {
      setDeletingProof(false);
    }
  };

  /** Clears the pending (not-yet-uploaded) file selection. */
  const handleClearPendingProof = () => {
    setPendingProofFile(null);
    if (pendingProofPreview) { URL.revokeObjectURL(pendingProofPreview); setPendingProofPreview(null); }
  };

  /** Opens the saved proof URL in a new browser tab (download). */
  const handleDownloadProof = () => {
    if (!savedProofUrl) return;
    const a = document.createElement('a');
    a.href = savedProofUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.download = savedProofFileName || 'payment_proof';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ─── Validate form ────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    if (!formData.customerName.trim()) { onShowToast('Customer name is required', 'error'); return false; }
    if (!formData.customerEmail.trim()) { onShowToast('Customer email is required', 'error'); return false; }
    if (formData.items.length === 0 || !formData.items[0].productId) {
      onShowToast('At least one item is required', 'error'); return false;
    }
    for (const item of formData.items) {
      if (item.productId) {
        const product = products.find(p => String(p.id) === String(item.productId));
        if (product?.hasVariants && product.variants?.length && !item.variantId) {
          onShowToast(`Variant selection is required for product: ${product.name || product.sku || 'product'}`, 'error');
          return false;
        }
      }
    }
    if (!formData.shippingAddress.fullName.trim()) { onShowToast('Shipping address full name is required', 'error'); return false; }
    if (!formData.shippingAddress.country.trim()) { onShowToast('Shipping country is required', 'error'); return false; }
    if (!formData.shippingAddress.state.trim()) { onShowToast('Shipping state is required', 'error'); return false; }
    if (!formData.shippingAddress.city.trim()) { onShowToast('Shipping city is required', 'error'); return false; }
    return true;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  /**
   * For ADD mode: the order is created first, then (if bank_transfer +
   * a pending file was selected) the proof is uploaded immediately after
   * the order document exists so we have the orderId for the storage path.
   *
   * For EDIT mode: the pending file upload is handled separately via the
   * Upload button in the UI (so the user has explicit control), and formData
   * already carries any previously saved proof metadata which updateOrder
   * will persist.
   */
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (mode === 'view') return;
    if (!validateForm()) return;

    try {
      setLoading(true);

      if (mode === 'edit' && order) {
        // A never-allocated ("pending") order moving to cancelled/returned needs
        // no warehouse pick or stock check — there's no stock to allocate, and
        // updateOrder already handles restoring stock (if any was allocated).
        // Only route through the warehouse-selection flow when the new status
        // actually requires allocated stock (e.g. confirmed/processing).
        const needsWarehouseAllocation = formData.status !== 'pending'
          && formData.status !== 'cancelled'
          && formData.status !== 'returned';
        if (initialStatus === 'pending' && needsWarehouseAllocation) {
          const stockCheck = await checkStockAvailability();
          if (!stockCheck?.isValid) {
            onShowToast(`Insufficient stock: ${stockCheck?.errors.join(', ') || 'Check failed'}`, 'error');
            setLoading(false);
            return;
          }
          setWarehouseDialogOpen(true);
          setLoading(false);
          return;
        }
        await OrderService.updateOrder(order.id!, formData, cancellationReason, returnReason, damageNotes);
        onShowToast('Order updated successfully', 'success');
        onSave();
        setLoading(false);
        return;
      }

      if (mode === 'add') {
        if (formData.status !== 'pending') {
          const stockCheck = await checkStockAvailability();
          if (!stockCheck?.isValid) {
            onShowToast(`Insufficient stock: ${stockCheck?.errors.join(', ') || 'Check failed'}`, 'error');
            setLoading(false);
            return;
          }
          setWarehouseDialogOpen(true);
          setLoading(false);
          return;
        }

        // Create order first to get the orderId
        const result = await OrderService.createOrder(formData, currentUser?.uid || '', undefined, undefined);

        // If bank transfer + a file was selected, upload it now that we have the orderId
        if (isBankTransfer && pendingProofFile && result.orderId) {
          try {
            setProofUploadProgress(0);
            const proofResult = await OrderService.uploadPaymentProof(
              pendingProofFile,
              result.orderId,
              (pct) => setProofUploadProgress(pct)
            );
            await OrderService.savePaymentProofToOrder(result.orderId, proofResult);
            setPendingProofFile(null);
            if (pendingProofPreview) { URL.revokeObjectURL(pendingProofPreview); setPendingProofPreview(null); }
          } catch (uploadErr: any) {
            // Order was created — warn but don't block
            console.error('Proof upload failed after order creation:', uploadErr);
            onShowToast('Order created but payment proof upload failed. Please re-upload in edit mode.', 'warning');
          } finally {
            setProofUploadProgress(null);
          }
        }

        onShowToast(
          result.needsAllocation
            ? 'Order created as pending. Please allocate warehouse when ready to process.'
            : 'Order created successfully',
          result.needsAllocation ? 'info' : 'success'
        );
        onSave();
      }
    } catch (err: any) {
      console.error('Error saving order:', err);
      onShowToast(err.message || 'Failed to save order', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleWarehouseSelection = async () => {
    try {
      setLoading(true);
      const validation = validateAllocations();
      if (!validation.isValid) {
        onShowToast(`Allocation errors:\n${validation.errors.join('\n')}`, 'error');
        setLoading(false);
        return;
      }
      if (mode === 'add') {
        const result = await OrderService.createOrder(formData, currentUser?.uid || '', undefined, itemAllocations);

        // Upload proof after creation if applicable
        if (isBankTransfer && pendingProofFile && result.orderId) {
          try {
            setProofUploadProgress(0);
            const proofResult = await OrderService.uploadPaymentProof(
              pendingProofFile, result.orderId, (pct) => setProofUploadProgress(pct)
            );
            await OrderService.savePaymentProofToOrder(result.orderId, proofResult);
            setPendingProofFile(null);
            if (pendingProofPreview) { URL.revokeObjectURL(pendingProofPreview); setPendingProofPreview(null); }
          } catch (uploadErr: any) {
            console.error('Proof upload failed:', uploadErr);
            onShowToast('Order created but payment proof upload failed. Please re-upload in edit mode.', 'warning');
          } finally {
            setProofUploadProgress(null);
          }
        }

        onShowToast('Order created and allocated successfully', 'success');
        onSave();
        // } else if (mode === 'edit' && order?.id) {
        //   await OrderService.manuallyAllocateOrder(order.id, itemAllocations);
        //   onShowToast('Warehouse allocation completed successfully', 'success');
        //   onSave();
        // }
      } else if (mode === 'edit' && order?.id) {
        await OrderService.manuallyAllocateOrder(order.id, itemAllocations, formData.status);
        // Also persist tracking number, estimated delivery, and other form fields
        await OrderService.updateOrder(order.id, formData, cancellationReason, returnReason, damageNotes);
        onShowToast('Warehouse allocation completed successfully', 'success');
        onSave();
      }
      setWarehouseDialogOpen(false);
    } catch (err: any) {
      onShowToast(err.message || 'Failed to allocate warehouse', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Refund save ──────────────────────────────────────────────────────────
  const handleSaveRefund = async () => {
    if (!order?.id) return;
    const amount = parseFloat(refundedAmount);
    if (refundStatus !== 'pending' && (isNaN(amount) || amount < 0)) {
      onShowToast('Please enter a valid refunded amount', 'error'); return;
    }
    if (refundStatus === 'partially_refunded' && amount >= order.totalAmount) {
      onShowToast('For a partial refund the amount must be less than the order total', 'error'); return;
    }
    if (refundStatus === 'partially_refunded' && amount <= 0) {
      onShowToast('Partial refund amount must be greater than zero', 'error'); return;
    }
    try {
      setRefundLoading(true);
      await OrderService.updateOrderRefundInfo(order.id, {
        refundStatus, refundedAmount: refundStatus === 'pending' ? 0 : amount, refundMethod,
      });
      onShowToast('Refund details saved', 'success');
    } catch (err: any) {
      onShowToast(`Failed to save refund: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setRefundLoading(false);
    }
  };

  // ─── Status-specific fields ───────────────────────────────────────────────
  const renderStatusSpecificFields = () => {
    if (formData.status === 'cancelled' && mode !== 'view') {
      return (
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Cancellation Reason</InputLabel>
            <Select value={cancellationReason} onChange={(e) => setCancellationReason(e.target.value as CancellationReason)} label="Cancellation Reason">
              {Object.entries(CANCELLATION_REASON_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth label="Additional Notes" value={damageNotes} onChange={(e) => setDamageNotes(e.target.value)}
            multiline rows={2} sx={{ mt: 2 }} placeholder="Add any additional notes about the cancellation..." />
        </Box>
      );
    }
    if (formData.status === 'returned' && mode !== 'view') {
      return (
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Return Reason</InputLabel>
            <Select value={returnReason} onChange={(e) => setReturnReason(e.target.value as ReturnReason)} label="Return Reason">
              {Object.entries(RETURN_REASON_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(returnReason === 'damaged' || returnReason === 'defective') && (
            <TextField fullWidth label="Damage Details" value={damageNotes} onChange={(e) => setDamageNotes(e.target.value)}
              multiline rows={3} sx={{ mt: 2 }} placeholder="Describe the damage or defect..." required />
          )}
          <TextField fullWidth label="Additional Notes"
            value={returnReason !== 'damaged' && returnReason !== 'defective' ? damageNotes : ''}
            onChange={(e) => setDamageNotes(e.target.value)}
            multiline rows={2} sx={{ mt: 2 }} placeholder="Add any additional notes about the return..." />
        </Box>
      );
    }
    return null;
  };

  // ─── Payment Proof Section ────────────────────────────────────────────────
  /**
   * Renders the payment proof upload/view panel.
   *
   * In ADD mode   — file picker + preview. Upload fires on Save.
   * In EDIT mode  — file picker + explicit Upload button (so user controls when).
   * In VIEW mode  — read-only display: image preview or PDF icon, + Download button.
   *
   * The section is only shown when payment method is bank_transfer.
   */
  const renderPaymentProofSection = () => {
    if (!isBankTransfer) return null;

    const hasSavedProof = !!savedProofUrl;

    // ── VIEW mode ────────────────────────────────────────────────────────────
    if (mode === 'view') {
      return (
        <Section>
          <SectionHeader
            icon={<AttachFileIcon fontSize="small" />}
            title="Payment Proof"
            subtitle="Uploaded proof of bank transfer payment"
            action={
              hasSavedProof ? (
                <Chip
                  size="small"
                  label={savedProofType === 'pdf' ? 'PDF' : 'Image'}
                  icon={savedProofType === 'pdf' ? <PictureAsPdfIcon /> : <ImageIcon />}
                  variant="outlined"
                  sx={getSoftChipSx('success')}
                />
              ) : null
            }
          />
          {hasSavedProof ? (
            <Box>
              {/* Image preview */}
              {savedProofType === 'image' && (
                <Box
                  sx={{
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    maxWidth: 480,
                  }}
                >
                  <img
                    src={savedProofUrl}
                    alt="Payment proof"
                    style={{ width: '100%', height: 'auto', display: 'block', maxHeight: 320, objectFit: 'contain' }}
                  />
                </Box>
              )}

              {/* PDF placeholder */}
              {savedProofType === 'pdf' && (
                <Box
                  sx={{
                    mb: 2,
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    maxWidth: 480,
                    bgcolor: 'rgba(211,47,47,0.04)',
                  }}
                >
                  <PictureAsPdfIcon sx={{ fontSize: 48, color: '#c62828' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {savedProofFileName || 'payment_proof.pdf'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">PDF Document</Typography>
                  </Box>
                </Box>
              )}

              {/* Download button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadProof}
                sx={{ mt: 0.5 }}
              >
                Download {savedProofType === 'pdf' ? 'PDF' : 'Image'}
              </Button>
            </Box>
          ) : (
            <Alert severity="info" icon={<AttachFileIcon />}>
              No payment proof has been uploaded for this order.
            </Alert>
          )}
        </Section>
      );
    }

    // ── ADD / EDIT mode ──────────────────────────────────────────────────────
    return (
      <Section>
        <SectionHeader
          icon={<AttachFileIcon fontSize="small" />}
          title="Payment Proof"
          subtitle={`Upload proof of bank transfer (images or PDF, max ${PAYMENT_PROOF_MAX_MB} MB)`}
        />

        {/* Already-saved attachment (edit mode) */}
        {hasSavedProof && (
          <Paper
            variant="outlined"
            sx={{
              p: 2, mb: 2, borderRadius: 2,
              borderColor: 'success.light',
              bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(102,187,106,0.08)' : 'rgba(102,187,106,0.06)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {savedProofType === 'image' ? (
                <Box
                  sx={{
                    width: 72, height: 72, borderRadius: 1, overflow: 'hidden', flexShrink: 0,
                    border: '1px solid', borderColor: 'divider',
                  }}
                >
                  <img src={savedProofUrl} alt="Saved proof" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ) : (
                <PictureAsPdfIcon sx={{ fontSize: 48, color: '#c62828', flexShrink: 0 }} />
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {savedProofFileName || (savedProofType === 'pdf' ? 'payment_proof.pdf' : 'payment_proof')}
                </Typography>
                <Typography variant="caption" color="success.main">
                  <CheckCircleIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  Saved
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Tooltip title="Download">
                  <IconButton size="small" onClick={handleDownloadProof} color="primary">
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete attachment">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleDeleteProof}
                    disabled={deletingProof}
                  >
                    {deletingProof ? <CircularProgress size={16} color="error" /> : <DeleteOutlineIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Pending file (selected but not yet uploaded) */}
        {pendingProofFile && (
          <Paper
            variant="outlined"
            sx={{
              p: 2, mb: 2, borderRadius: 2,
              borderColor: 'warning.light',
              bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,167,38,0.08)' : 'rgba(255,167,38,0.06)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Thumbnail for images, icon for PDF */}
              {pendingProofFile.type !== 'application/pdf' && pendingProofPreview ? (
                <Box sx={{ width: 72, height: 72, borderRadius: 1, overflow: 'hidden', flexShrink: 0, border: '1px solid', borderColor: 'divider' }}>
                  <img src={pendingProofPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ) : (
                <PictureAsPdfIcon sx={{ fontSize: 48, color: '#c62828', flexShrink: 0 }} />
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{pendingProofFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(pendingProofFile.size / 1024 / 1024).toFixed(2)} MB
                  {mode === 'add' ? ' · Will be uploaded on save' : ' · Ready to upload'}
                </Typography>
                {/* Upload progress bar */}
                {proofUploadProgress !== null && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress variant="determinate" value={proofUploadProgress} sx={{ borderRadius: 1 }} />
                    <Typography variant="caption" color="text.secondary">{proofUploadProgress}%</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                {/* Explicit upload button only in edit mode */}
                {mode === 'edit' && order?.id && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={proofUploadProgress !== null ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
                    onClick={handleUploadProof}
                    disabled={proofUploadProgress !== null}
                  >
                    {proofUploadProgress !== null ? `${proofUploadProgress}%` : 'Upload'}
                  </Button>
                )}
                <Tooltip title="Remove selection">
                  <IconButton size="small" color="error" onClick={handleClearPendingProof} disabled={proofUploadProgress !== null}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Pick-file button — hidden when a pending file is already selected */}
        {!pendingProofFile && (
          <Box>
            <input
              ref={proofInputRef}
              type="file"
              accept={PAYMENT_PROOF_ACCEPT}
              style={{ display: 'none' }}
              onChange={handleProofFileSelect}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={() => proofInputRef.current?.click()}
              sx={{ borderStyle: 'dashed' }}
            >
              {hasSavedProof ? 'Replace Attachment' : 'Attach Payment Proof'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Accepted: JPEG, PNG, WebP, GIF, PDF — max {PAYMENT_PROOF_MAX_MB} MB
            </Typography>
          </Box>
        )}
      </Section>
    );
  };

  // ─── Warehouse allocation section (view mode) ─────────────────────────────
  const renderWarehouseAllocationSection = () => {
    if (mode !== 'view' || !order?.isWarehouseAllocated || !order.items) return null;

    const warehouseGroups = order.items.reduce((acc, item) => {
      const whId = item.sourceWarehouseId || 'unallocated';
      if (!acc[whId]) acc[whId] = { warehouseName: item.sourceWarehouseName || 'Unallocated', items: [] };
      acc[whId].items.push(item);
      return acc;
    }, {} as Record<string, { warehouseName: string; items: typeof order.items }>);

    const groupEntries = Object.entries(warehouseGroups);

    return (
      <Section>
        <SectionHeader
          icon={<WarehouseIcon fontSize="small" />}
          title="Warehouse Allocation"
          subtitle={`Allocated across ${groupEntries.length} warehouse${groupEntries.length !== 1 ? 's' : ''}`}
          action={<Chip icon={<CheckCircleIcon />} label="Allocated" size="small" variant="outlined" color="primary" sx={getSoftChipSx('success')} />}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groupEntries.map(([warehouseId, group]) => (
            <Paper key={warehouseId} variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <WarehouseIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">{group.warehouseName}</Typography>
                <Chip label={`${formatQuantity(group.items.reduce((sum, i) => sum + i.quantity, 0))} units`} size="small" variant="outlined" color="primary" sx={{ ml: 'auto' }} />
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem' }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem' }} align="right">Qty</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.items.map((item, idx) => {
                      const variantLabel = item.variantSelections?.length ? item.variantSelections.map((s: any) => s.variantValue).join(', ') : null;
                      return (
                        <TableRow key={idx} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: 'action.hover' } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium" color="text.primary">{item.productName}</Typography>
                            {variantLabel && <Typography variant="caption" color="text.secondary">{variantLabel}</Typography>}
                            {!!item.serialNumbers?.length && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {item.serialNumbers.map(serial => (
                                  <Chip key={serial} label={serial} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="text.primary">{formatQuantity(item.quantity)}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Box>
        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'success.light', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(102,187,106,0.12)' : 'rgba(102,187,106,0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon fontSize="small" color="success" />
          <Typography variant="body2" color="success.main">
            All <strong>{formatQuantity(order.items.reduce((sum, i) => sum + i.quantity, 0))} items</strong> successfully
            allocated from <strong>{groupEntries.length}</strong> warehouse{groupEntries.length !== 1 ? 's' : ''}.
          </Typography>
        </Box>
      </Section>
    );
  };

  // ─── Refund section ───────────────────────────────────────────────────────
  const renderRefundSection = () => {
    if (mode !== 'view' || !order) return null;
    const orderWithRefund = order as OrderWithRefund;
    const hasRefundInfo = orderWithRefund.refundStatus && orderWithRefund.refundStatus !== 'pending';
    const isReturned = order.status === 'returned';
    if (!isReturned && !hasRefundInfo) return null;

    return (
      <Section>
        <SectionHeader
          icon={<PaymentsIcon fontSize="small" />}
          title="Refund Details"
          subtitle="Payment refund status for this order"
          action={orderWithRefund.refundStatus ? (
            <Chip label={REFUND_STATUS_LABELS[orderWithRefund.refundStatus]} size="small" variant="outlined" sx={getSoftChipSx(refundStatusColor(orderWithRefund.refundStatus))} />
          ) : null}
        />
        {hasRefundInfo && (
          <Box sx={{ mb: 2, p: 1.5, borderLeft: '3px solid', pl: 2, borderColor: orderWithRefund.refundStatus === 'refunded' ? 'success.main' : orderWithRefund.refundStatus === 'partially_refunded' ? 'warning.main' : 'info.main' }}>
            <Typography variant="body2">
              {orderWithRefund.refundStatus === 'partially_refunded'
                ? <>Partially refunded: <strong>{formatCurrency(orderWithRefund.refundedAmount ?? 0)}</strong> of {formatCurrency(order.totalAmount)}</>
                : <>Full refund of <strong>{formatCurrency(orderWithRefund.refundedAmount ?? order.totalAmount)}</strong> issued</>
              }
            </Typography>
            {orderWithRefund.refundDate && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                on {formatDate(orderWithRefund.refundDate)}
                {orderWithRefund.refundMethod && ` · ${REFUND_METHOD_LABELS[orderWithRefund.refundMethod]}`}
              </Typography>
            )}
            {orderWithRefund.refundTransactionId && (
              <Typography variant="caption" display="block" color="text.secondary">
                Ref: {orderWithRefund.refundTransactionId}
              </Typography>
            )}
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Refund Status</InputLabel>
              <Select value={refundStatus}
                onChange={e => {
                  const v = e.target.value as RefundStatus;
                  setRefundStatus(v);
                  if (v === 'refunded') setRefundedAmount(String(order.totalAmount));
                  if (v === 'pending') setRefundedAmount('0');
                }}
                label="Refund Status">
                {(Object.keys(REFUND_STATUS_LABELS) as RefundStatus[]).map(k => (
                  <MenuItem key={k} value={k}>{REFUND_STATUS_LABELS[k]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Amount Refunded" type="number"
              value={refundedAmount} onChange={e => setRefundedAmount(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
              helperText={`Order total: ${formatCurrency(order.totalAmount)}`}
              disabled={!refundAmountEditable} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Refund Method</InputLabel>
              <Select value={refundMethod} onChange={e => setRefundMethod(e.target.value as RefundMethod)}
                label="Refund Method" disabled={!refundAmountEditable}>
                {(Object.keys(REFUND_METHOD_LABELS) as RefundMethod[]).map(k => (
                  <MenuItem key={k} value={k}>{REFUND_METHOD_LABELS[k]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Refund Date" type="date"
              value={refundDate} onChange={e => setRefundDate(e.target.value)}
              InputLabelProps={{ shrink: true }} disabled={!refundAmountEditable} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Transaction / Reference ID"
              value={refundTransactionId} onChange={e => setRefundTransactionId(e.target.value)}
              placeholder="Bank ref, gateway ID, etc." disabled={!refundAmountEditable} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Refund Notes"
              value={refundNotes} onChange={e => setRefundNotes(e.target.value)}
              multiline rows={2} placeholder="Additional notes about this refund…" />
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleSaveRefund} variant="contained" size="small"
              disabled={refundLoading}
              startIcon={refundLoading ? <CircularProgress size={14} /> : <SaveIcon />}>
              {refundLoading ? 'Saving…' : 'Save Refund'}
            </Button>
          </Grid>
        </Grid>
      </Section>
    );
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const getTitle = () => {
    switch (mode) {
      case 'add': return 'Add New Order';
      case 'edit': return 'Edit Order';
      case 'view': return 'Order Details';
      default: return '';
    }
  };

  const getTotalAmount = () => {
    const subtotal = parseFloat(formData.subtotal) || 0;
    const taxAmount = parseFloat(formData.taxAmount) || 0;
    const shippingCost = parseFloat(formData.shippingCost) || 0;
    return Math.max(0, subtotal + taxAmount + shippingCost);
  };

  const getSelectedProduct = (productId: string): Product | undefined =>
    products.find(p => String(p.id) === String(productId));

  const getVariantDisplayName = (variant: ProductVariant): string => {
    if (!variant.variantSelections?.length) return 'Default Variant';
    return variant.variantSelections.map(s => `${s.variantType}: ${s.variantValue}`).join(', ');
  };

  const handleVariantChange = (index: number, variantKey: string) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], variantId: variantKey };
    setFormData(prev => ({ ...prev, items: newItems }));
    calculateTotals(newItems);
    _applyItemsDiscount(newItems);
  };

  // ─── Loading state ────────────────────────────────────────────────────────
  if (initialLoading && isOpen) {
    return (
      <Drawer anchor="right" open={isOpen} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: '90%', md: 900 } } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>{getTitle()}</Typography>
            <IconButton onClick={onClose} edge="end"><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </Box>
      </Drawer>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: '90%', md: 900 } } }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>{getTitle()}</Typography>
          <IconButton onClick={onClose} edge="end"><CloseIcon /></IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>

            {order?.stockReservationFailed && (
              <Alert severity="error" sx={{ mb: 3 }}>
                Stock could not be automatically reserved for this order{order.stockReservationError ? `: ${order.stockReservationError}` : ''}.
                {' '}Please allocate a warehouse manually below before confirming or shipping this order.
              </Alert>
            )}

            {/* ── Customer Information ─────────────────────────────────── */}
            <Section>
              <SectionHeader icon={<PersonIcon fontSize="small" />} title="Customer Information" />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Customer Name" name="customerName"
                    value={formData.customerName} onChange={handleInputChange} disabled={mode === 'view'} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email Address" name="customerEmail" type="email"
                    value={formData.customerEmail} onChange={handleInputChange} disabled={mode === 'view'} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone Number" name="customerPhone"
                    value={formData.customerPhone} onChange={handleInputChange} disabled={mode === 'view'} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="WhatsApp Number" name="customerWhatsApp"
                    value={formData.customerWhatsApp || ''} onChange={handleInputChange} disabled={mode === 'view'} />
                </Grid>
              </Grid>
            </Section>

            {/* ── Order Items ──────────────────────────────────────────── */}
            <Section>
              <SectionHeader
                icon={<CardGiftcard fontSize="small" />}
                title="Order Items"
                action={mode !== 'view' && (
                  <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addItem}>Add Item</Button>
                )}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {formData.items.map((item, index) => {
                  const selectedProduct = getSelectedProduct(item.productId);
                  const hasVariants = selectedProduct?.hasVariants && selectedProduct.variants?.length;

                  const itemDiscountPct = (() => {
                    if (!selectedProduct) return 0;
                    if (item.variantId && selectedProduct.variants) {
                      const variant = selectedProduct.variants.find(v => getVariantKey(v) === item.variantId);
                      if (variant) return variant.discount || 0;
                    }
                    return selectedProduct.discount || 0;
                  })();

                  return (
                    <Box key={index} sx={{ py: 1 }}>
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} sm={hasVariants ? 5 : 6}>
                          <FormControl fullWidth>
                            <InputLabel>Product</InputLabel>
                            <Select value={item.productId} label="Product"
                              onChange={(e) => handleItemChange(index, 'productId', String(e.target.value))}
                              disabled={mode === 'view'}>
                              <MenuItem value=""><em>Select a product</em></MenuItem>
                              {products.map(product => {
                                const outOfStock = isProductOutOfStock(product);
                                return (
                                  <MenuItem key={String(product.id)} value={String(product.id)}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                        {product.name} — {product.sku}
                                        {product.hasVariants ? ' (Has Variants)' : ''}
                                      </Typography>
                                      {outOfStock && (
                                        <Chip
                                          label="Out of Stock"
                                          size="small"
                                          icon={<BlockIcon sx={{ fontSize: '0.75rem !important' }} />}
                                          sx={{
                                            fontSize: '0.65rem', height: 20,
                                            bgcolor: 'rgba(211,47,47,0.10)', borderColor: 'rgba(211,47,47,0.35)',
                                            color: '#c62828', border: '1px solid rgba(211,47,47,0.35)',
                                            '& .MuiChip-icon': { color: '#c62828' },
                                            '& .MuiChip-label': { color: '#c62828' },
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>

                        {hasVariants && (
                          <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                              <InputLabel>Variant</InputLabel>
                              <Select value={item.variantId || ''} label="Variant" required
                                onChange={(e) => handleVariantChange(index, e.target.value as string)}
                                disabled={mode === 'view'}
                                renderValue={(selectedValue) => {
                                  if (!selectedValue) return <em>Select variant</em>;
                                  const selectedVariant = selectedProduct?.variants?.find(v => getVariantKey(v) === selectedValue);
                                  return selectedVariant
                                    ? <Typography variant="body2">{getVariantDisplayName(selectedVariant)}</Typography>
                                    : <em>Select variant</em>;
                                }}>
                                <MenuItem value=""><em>Select variant</em></MenuItem>
                                {selectedProduct?.variants?.map(variant => {
                                  const vKey = getVariantKey(variant);
                                  const variantOutOfStock = variant.isOutOfStock || variant.stockQuantity <= 0;
                                  return (
                                    <MenuItem key={vKey} value={vKey}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        <Box sx={{ flexGrow: 1 }}>
                                          <Typography variant="body2">{getVariantDisplayName(variant)}</Typography>
                                          <Typography variant="caption" color="text.secondary">{formatPriceDisplay(variant.price)}</Typography>
                                        </Box>
                                        {variantOutOfStock && (
                                          <Chip
                                            label="Out of Stock"
                                            size="small"
                                            icon={<BlockIcon sx={{ fontSize: '0.75rem !important' }} />}
                                            sx={{
                                              fontSize: '0.6rem', height: 18,
                                              bgcolor: 'rgba(211,47,47,0.10)', borderColor: 'rgba(211,47,47,0.35)',
                                              color: '#c62828', border: '1px solid rgba(211,47,47,0.35)',
                                              '& .MuiChip-icon': { color: '#c62828' },
                                              '& .MuiChip-label': { color: '#c62828' },
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                        <Grid item xs={6} sm={hasVariants ? 2 : 3}>
                          <TextField fullWidth label="Quantity" type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            disabled={mode === 'view'} inputProps={{ min: 1 }} />
                        </Grid>

                        {mode !== 'view' && formData.items.length > 1 && (
                          <Grid item xs={12} sm={hasVariants ? 1 : 2}>
                            <IconButton color="error" onClick={() => removeItem(index)} sx={{ mt: 1 }}>
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>

                      {mode === 'view' && item.variantId && selectedProduct?.variants && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Selected Variant:{' '}
                            {getVariantDisplayName(
                              selectedProduct.variants.find(v =>
                                getVariantKey(v) === item.variantId ||
                                (v.id && String(v.id) === item.variantId) ||
                                (v.sku && v.sku === item.variantId)
                              )!
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Section>

            {/* ── Shipping Address ─────────────────────────────────────── */}
            <Section>
              <SectionHeader title="Shipping Address" />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Full Name" value={formData.shippingAddress.fullName}
                    onChange={(e) => handleAddressChange('shippingAddress', 'fullName', e.target.value)} disabled={mode === 'view'} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone Number" value={formData.shippingAddress.phoneNumber || ''}
                    onChange={(e) => handleAddressChange('shippingAddress', 'phoneNumber', e.target.value)} disabled={mode === 'view'} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required disabled={mode === 'view'}>
                    <InputLabel>Country</InputLabel>
                    <Select value={formData.shippingAddress.country} label="Country"
                      onChange={(e) => handleAddressChange('shippingAddress', 'country', e.target.value)}>
                      <MenuItem value=""><em>Select a country</em></MenuItem>
                      {loadingShipping
                        ? <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading countries...</MenuItem>
                        : shippingCountries.map(country => (
                          <MenuItem key={country.id} value={country.name}>{country.name} ({country.code})</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required disabled={mode === 'view' || !formData.shippingAddress.country}>
                    <InputLabel>State / Province</InputLabel>
                    <Select value={formData.shippingAddress.state} label="State / Province"
                      onChange={(e) => handleAddressChange('shippingAddress', 'state', e.target.value)}>
                      <MenuItem value=""><em>Select a state</em></MenuItem>
                      {shippingStates.length === 0 && formData.shippingAddress.country && (
                        <MenuItem disabled>No states available for this country</MenuItem>
                      )}
                      {shippingStates.map(state => (
                        <MenuItem key={state.id} value={state.name}>
                          {state.name}
                          {state.shippingCost && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({formatCurrency(state.shippingCost)} shipping)
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="City" value={formData.shippingAddress.city}
                    onChange={(e) => handleAddressChange('shippingAddress', 'city', e.target.value)} disabled={mode === 'view'} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Postal Code" value={formData.shippingAddress.postalCode}
                    onChange={(e) => handleAddressChange('shippingAddress', 'postalCode', e.target.value)} disabled={mode === 'view'} required />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Address Line 1" value={formData.shippingAddress.addressLine1}
                    onChange={(e) => handleAddressChange('shippingAddress', 'addressLine1', e.target.value)} disabled={mode === 'view'} required />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Address Line 2" value={formData.shippingAddress.addressLine2 || ''}
                    onChange={(e) => handleAddressChange('shippingAddress', 'addressLine2', e.target.value)} disabled={mode === 'view'} />
                </Grid>
              </Grid>
              {mode !== 'view' && (
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={<Checkbox checked={useSameAddress} onChange={handleUseSameAddressChange} />}
                    label="Use same address for billing" />
                </Box>
              )}
            </Section>

            {/* ── Billing Address ──────────────────────────────────────── */}
            {(!useSameAddress || mode === 'view') && (
              <Section>
                <SectionHeader title="Billing Address" />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Full Name" value={formData.billingAddress.fullName}
                      onChange={(e) => handleAddressChange('billingAddress', 'fullName', e.target.value)}
                      disabled={mode === 'view'} required={!useSameAddress} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Phone Number" value={formData.billingAddress.phoneNumber || ''}
                      onChange={(e) => handleAddressChange('billingAddress', 'phoneNumber', e.target.value)} disabled={mode === 'view'} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={mode === 'view'}>
                      <InputLabel>Country</InputLabel>
                      <Select value={formData.billingAddress.country} label="Country"
                        onChange={(e) => handleAddressChange('billingAddress', 'country', e.target.value)}>
                        <MenuItem value=""><em>Select a country</em></MenuItem>
                        {loadingShipping
                          ? <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading countries...</MenuItem>
                          : shippingCountries.map(country => (
                            <MenuItem key={country.id} value={country.name}>{country.name} ({country.code})</MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={mode === 'view' || !formData.billingAddress.country}>
                      <InputLabel>State / Province</InputLabel>
                      <Select value={formData.billingAddress.state} label="State / Province"
                        onChange={(e) => handleAddressChange('billingAddress', 'state', e.target.value)}>
                        <MenuItem value=""><em>Select a state</em></MenuItem>
                        {billingStates.length === 0 && formData.billingAddress.country && (
                          <MenuItem disabled>No states available for this country</MenuItem>
                        )}
                        {billingStates.map(state => (
                          <MenuItem key={state.id} value={state.name}>{state.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="City" value={formData.billingAddress.city}
                      onChange={(e) => handleAddressChange('billingAddress', 'city', e.target.value)}
                      disabled={mode === 'view'} required={!useSameAddress} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Postal Code" value={formData.billingAddress.postalCode}
                      onChange={(e) => handleAddressChange('billingAddress', 'postalCode', e.target.value)}
                      disabled={mode === 'view'} required={!useSameAddress} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Address Line 1" value={formData.billingAddress.addressLine1}
                      onChange={(e) => handleAddressChange('billingAddress', 'addressLine1', e.target.value)}
                      disabled={mode === 'view'} required={!useSameAddress} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Address Line 2" value={formData.billingAddress.addressLine2 || ''}
                      onChange={(e) => handleAddressChange('billingAddress', 'addressLine2', e.target.value)} disabled={mode === 'view'} />
                  </Grid>
                </Grid>
              </Section>
            )}

            {/* ── Order Totals ─────────────────────────────────────────── */}
            <Section>
              <SectionHeader icon={<Calculate fontSize="small" />} title="Order Totals" />
              {mode !== 'view' && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {companyTaxRate > 0 ? `Tax is pre-filled from your company profile (${formatNumber(companyTaxRate, 2)}%). ` : 'No company tax rate configured. '}
                  {parseFloat(discountPercentage) > 0 ? `Discount is auto-filled from the selected product(s) (${formatNumber(parseFloat(discountPercentage), 2)}%). ` : 'No product discount applied yet. '}
                  You can override both values below.
                </Typography>
              )}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ mb: 3 }}>
                    <ToggleButtonGroup value={taxType} exclusive onChange={handleTaxTypeChange} size="small" fullWidth disabled={mode === 'view'}>
                      <ToggleButton value="percentage"><PercentIcon sx={{ mr: 0.5, fontSize: 18 }} /> Percentage</ToggleButton>
                      <ToggleButton value="fixed">{currencySymbol} Fixed</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {taxType === 'percentage' ? (
                    <TextField fullWidth label="Tax Percentage" type="number"
                      value={taxPercentage} onChange={handleTaxPercentageChange}
                      disabled={mode === 'view'} inputProps={{ step: 0.01, min: 0, max: 100 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      helperText={companyTaxRate > 0 && mode !== 'view' ? `Company default: ${formatNumber(companyTaxRate, 2)}%` : undefined} />
                  ) : (
                    <TextField fullWidth label={`Tax Amount (${currencySymbol})`} type="number"
                      value={formData.taxAmount} onChange={handleTaxFixedChange}
                      disabled={mode === 'view'} inputProps={{ step: 0.01, min: 0 }} />
                  )}
                  {taxType === 'percentage' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Tax: {formatCurrency(formData.taxAmount)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ mb: 3 }}>
                    <ToggleButtonGroup value={discountType} exclusive onChange={handleDiscountTypeChange} size="small" fullWidth disabled={mode === 'view'}>
                      <ToggleButton value="percentage"><PercentIcon sx={{ mr: 0.5, fontSize: 18 }} /> Percentage</ToggleButton>
                      <ToggleButton value="fixed">{currencySymbol} Fixed</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {discountType === 'percentage' ? (
                    <TextField fullWidth label="Discount Percentage" type="number"
                      value={discountPercentage} onChange={handleDiscountPercentageChange}
                      disabled={mode === 'view'} inputProps={{ step: 0.01, min: 0, max: 100 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      helperText={parseFloat(discountPercentage) > 0 && mode !== 'view' ? 'Auto-filled from product discount' : undefined} />
                  ) : (
                    <TextField fullWidth label={`Discount (${currencySymbol})`} type="number"
                      value={formData.discountAmount} onChange={handleDiscountFixedChange}
                      disabled={mode === 'view'} inputProps={{ step: 0.01, min: 0 }} />
                  )}
                  {discountType === 'percentage' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Discount: {formatCurrency(formData.discountAmount)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label={`Shipping Cost (${currencySymbol})`} name="shippingCost" type="number"
                    value={formData.shippingCost} onChange={handleInputChange}
                    disabled={mode === 'view'} inputProps={{ step: 0.01, min: 0 }} sx={{ mt: 7.8 }} />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Total Amount:</Typography>
                    <Typography variant="h5" color="primary" fontWeight="bold">{formatCurrency(getTotalAmount())}</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Subtotal (after discount): {formatCurrency(formData.subtotal)} &nbsp;·&nbsp;
                Tax: {formatCurrency(formData.taxAmount)} &nbsp;·&nbsp;
                Shipping: {formatCurrency(formData.shippingCost)} &nbsp;·&nbsp;
                You saved: {formatCurrency(formData.discountAmount)}
              </Typography>
            </Section>

            {/* ── Order Status ─────────────────────────────────────────── */}
            <Section>
              <SectionHeader title="Order Status" />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Order Status</InputLabel>
                    <Select name="status" value={formData.status} label="Order Status"
                      onChange={handleSelectChange('status')} disabled={mode === 'view'}>
                      {Object.entries(ORDER_STATUS_LABELS)
                        .filter(([value]) => value !== 'returned')
                        .map(([value, label]) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Status</InputLabel>
                    <Select name="paymentStatus" value={formData.paymentStatus} label="Payment Status"
                      onChange={handleSelectChange('paymentStatus')} disabled={mode === 'view'}>
                      {Object.entries(PAYMENT_STATUS_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select name="paymentMethod" value={formData.paymentMethod} label="Payment Method"
                      onChange={handleSelectChange('paymentMethod')} disabled={mode === 'view'}>
                      {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {(formData.status === 'cancelled' || formData.status === 'returned') && mode !== 'view' && (
                  <Grid item xs={12}>{renderStatusSpecificFields()}</Grid>
                )}
              </Grid>
            </Section>

            {/* ── Payment Proof (bank_transfer only) ───────────────────── */}
            {renderPaymentProofSection()}

            {/* ── Warehouse Allocation (view mode) ─────────────────────── */}
            {renderWarehouseAllocationSection()}

            {/* ── Refund Details (view mode) ────────────────────────────── */}
            {renderRefundSection()}

            {/* ── Additional Information ───────────────────────────────── */}
            <Section>
              <SectionHeader title="Additional Information" />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Tracking Number" name="trackingNumber"
                    value={formData.trackingNumber} onChange={handleInputChange} disabled={mode === 'view'} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Estimated Delivery" name="estimatedDelivery" type="date"
                    value={formData.estimatedDelivery} onChange={handleInputChange}
                    disabled={mode === 'view'} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Order Notes" name="notes"
                    value={formData.notes} onChange={handleInputChange}
                    disabled={mode === 'view'} multiline rows={3}
                    placeholder="Add any notes about this order..." />
                </Grid>
              </Grid>
            </Section>

            {/* ── Order Summary (view mode) ─────────────────────────────── */}
            {mode === 'view' && order && (
              <Section>
                <SectionHeader title="Order Summary" />
                <List disablePadding>
                  <ListItem disableGutters>
                    <ListItemText primary="Order Number" secondary={order.orderNumber} />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemText primary="Order Date" secondary={order.orderDate.toLocaleDateString()} />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemText primary="Items Count" secondary={formatQuantity(order.items.length)} />
                  </ListItem>
                  <Divider />
                  <ListItem disableGutters sx={{ mt: 1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="h6" component="span">
                          Total Amount: {formatCurrency(order.totalAmount)}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Section>
            )}

          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: mode === 'view' ? 'space-between' : 'flex-end', gap: 2 }}>
            {mode === 'view' ? (
              <>
                <Button variant="outlined" color="success"
                  startIcon={downloadingInvoice ? <CircularProgress size={20} /> : <ReceiptIcon />}
                  onClick={handleDownloadInvoice} disabled={downloadingInvoice}>
                  {downloadingInvoice ? 'Generating...' : 'Download Invoice'}
                </Button>
                <Button variant="outlined" onClick={onClose}>Close</Button>
              </>
            ) : (
              <>
                <Button variant="outlined" onClick={onClose} disabled={loading}>Cancel</Button>
                <Button variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSubmit} disabled={loading}>
                  {mode === 'add' ? 'Create Order' : 'Update Order'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Full-screen operation overlay ─────────────────────────────────── */}
      <Backdrop
        open={loading || refundLoading || downloadingInvoice}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, px: 5, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, minWidth: 280, boxShadow: 24 }}>
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {downloadingInvoice ? 'Generating Invoice…' : refundLoading ? 'Saving Refund…' : mode === 'add' ? 'Creating Order…' : 'Updating Order…'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      {/* ── Warehouse Selection Dialog ────────────────────────────────────── */}
      <WarehouseSelectionDialog
        open={warehouseDialogOpen}
        loading={loading}
        itemAllocations={itemAllocations}
        stockAvailability={stockAvailability}
        currencySymbol={currencySymbol}
        onClose={() => setWarehouseDialogOpen(false)}
        onConfirm={handleWarehouseSelection}
        onUpdateAllocationQuantity={updateAllocationQuantity}
        onAddWarehouse={addWarehouseToItem}
        onRemoveWarehouse={removeWarehouseFromItem}
        validateAllocations={validateAllocations}
      />
    </Drawer>
  );
};

export default OrderDrawer;